import { Breakpoint } from '@mui/system';
import { useFeatureFlags, usePermissions, validateRestriction } from '@procureai/permissions-ui';
import { complement } from 'ramda';
import { useContext, useEffect, useState } from 'react';

import { appConfig } from '~/common/config';
import { BaseModule } from '~/modules';

import { ModulePageConfigContext } from './ModulePageConfigProvider.context';
import { ModulePageConfigState, ModuleTabOptions } from './ModulePageConfigProvider.types';
import { getTabBySlug } from './ModulePageConfigProvider.utils';

interface UseModuleConfigFetchResult {
  loading: boolean;
  modules: ModulePageConfigState;
}

export const useModulePageConfigFetch = (): UseModuleConfigFetchResult => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<ModulePageConfigState | null>(null);

  useEffect(() => {
    if (!config) {
      const fetchData = async () => {
        try {
          const response = await fetch(`/modules.json?v=${appConfig.version}`, { credentials: 'include' });
          const data = (await response.json()) || {};
          setConfig(data);
          setLoading(false);
        } catch (error) {
          console.log('error', error);
        }
      };
      fetchData();
    }
  }, [config]);

  return {
    modules: config || {},
    loading: loading,
  };
};

export interface UseModulePageConfigProps {
  pageId: string;
  tabId?: string;
}

export interface UseModulePageConfigResult {
  modules: BaseModule[];
  tabs?: ModuleTabOptions[];
  maxWidth?: Breakpoint | false;
}

export const useModulePageConfig = ({ pageId, tabId }: UseModulePageConfigProps): UseModulePageConfigResult => {
  const value = useContext(ModulePageConfigContext);
  const { hasPermission } = usePermissions();
  const { hasFeatureFlag } = useFeatureFlags();
  const page = value[pageId];

  if (page) {
    if (page.tabs) {
      const accessibleTabs = page.tabs.filter(
        ({ needs, lacks }) =>
          validateRestriction(needs, hasPermission, hasFeatureFlag) &&
          validateRestriction(lacks, complement(hasPermission), complement(hasFeatureFlag)),
      );
      const tab = getTabBySlug(tabId, accessibleTabs);

      if (tab) {
        return { modules: tab.modules, tabs: accessibleTabs, maxWidth: tab.maxWidth };
      }
    } else if (page.modules) {
      return { modules: page.modules };
    }
  }

  return { modules: [] };
};
